import { createTableV2RenderBinding } from '@databases/frontend/table-v2';
import { type TableV2RenderSnapshot } from '@databases/frontend/table-v2-host';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import {
  createTableV2Instance,
  type TableV2DataSource,
} from '@databases/view-layout-table-v2/data';
import { scrollTableV2CellIntoView } from '@databases/view-layout-table-v2/dom';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';
import { createTableV2MouseInputAdapter } from '../features/selections/create-table-v2-mouse-input-adapter.ts';
import { TableV2SelectionDatabaseAdapter } from '../features/selections/table-v2-selection-database-adapter.ts';
import { type SelectionLayoutDependencies } from './selection-layout-dependencies.ts';

/**
 * One V2 view's captured row/column ordering, shared by drawing and selection.
 * Its lifetime follows the database and view, not React mounts or permission
 * changes. Disposal revokes its lazy entry readers; it does not dispose the SDK,
 * selection machine or highlights controller.
 */
export interface TableV2ViewSession {
  readonly renderSnapshot: TableV2RenderSnapshot;
  readonly selectionDependencies: SelectionLayoutDependencies;
  dispose(): void;
}

/**
 * Captures ordering before the lazy renderer mounts, so keyboard navigation and
 * drawing agree on which entry/field each grid position means. Entry values are
 * still read on demand. The application router calls this only for selected V2.
 *
 * An absent source produces unavailable rendering and read-only selection
 * dependencies, rather than borrowing another layout's adapter. This is an
 * initial capture; it does not subscribe to subsequent database changes.
 */
export function createTableV2ViewSession({
  source,
  contentId,
  fieldVisibility,
  getSpaceKey,
  subscribePermissions,
}: {
  contentId?: string;
  source: TableV2DataSource | undefined;
  fieldVisibility: DatabaseViewSettings['fields'];
  getSpaceKey: () => string;
  subscribePermissions?: (listener: VoidFunction) => VoidFunction;
}): TableV2ViewSession {
  const tableData = source ? createTableV2Instance(source, fieldVisibility) : undefined;

  let resourceController = new AbortController();
  const resourceListeners = new Set<VoidFunction>();
  let disposed = false;

  const unsubscribePermissions = tableData
    ? subscribePermissions?.(() => {
        if (disposed) {
          return;
        }

        resourceController.abort();
        resourceController = new AbortController();

        for (const listener of resourceListeners) {
          listener();
        }
      })
    : undefined;

  const resources = {
    getSnapshot: () => resourceController.signal,
    subscribe(listener: VoidFunction) {
      resourceListeners.add(listener);

      return () => {
        resourceListeners.delete(listener);
      };
    },
  };

  return {
    renderSnapshot: tableData
      ? createTableV2RenderBinding(tableData, contentId, resources)
      : { status: 'unavailable' },
    selectionDependencies: {
      databaseAdapter: new TableV2SelectionDatabaseAdapter({
        projection: tableData?.projection,
        getEntry: (entryId) => tableData?.getEntry(entryId),
        getSpaceKey,
      }),
      viewChanges: {
        isAvailable: () => tableData !== undefined,
        subscribe: () => () => {},
      },
      mouseInputAdapter: createTableV2MouseInputAdapter(),
      canEditContent: false,
      featureLayoutTypes: [TABLE_V2_LAYOUT_ID],
      selectionUiMetricsEnabled: false,
      scrollCellIntoView(cell) {
        const scrollContainer = document.getElementById('scrollable-inner-container');

        if (scrollContainer) {
          scrollTableV2CellIntoView(scrollContainer, cell.row.index, cell.col.index);
        }
      },
    },
    dispose: () => {
      if (disposed) {
        return;
      }

      disposed = true;
      unsubscribePermissions?.();
      resourceController.abort();
      resourceListeners.clear();
      tableData?.dispose();
    },
  };
}
